import { createPersistedStore, storage } from '@connect-x-app/hybrid-utils';
import { Notification, NotificationStatus } from '@connect-x-app/shared-domain-models';
import { createSerializableStorage } from '@connect-x-app/shared-utils';

type NotificationState = {
  addNotification: (notification: Notification) => void;
  markNotificationAsDismissed: (notificationId: string) => void;
  notifications: Map<string, Notification>;
  removeNotificationById: (notificationId: string) => void;
  setNotifications: (notifications: Notification[], unreadCount: number) => void;
  unreadNotificationsCount: number;
};

export const useNotificationStore = createPersistedStore<NotificationState>(
  (set, _) => ({
    addNotification: notification =>
      set(state => {
        if (state.notifications.has(notification.id)) {
          return {};
        }

        const updated = new Map(state.notifications);
        updated.set(notification.id, notification);

        const unreadNotificationsCount =
          notification.status === NotificationStatus.SENT ? state.unreadNotificationsCount + 1 : state.unreadNotificationsCount;

        return { notifications: updated, unreadNotificationsCount };
      }),
    markNotificationAsDismissed: notificationId =>
      set(state => {
        const existing = state.notifications.get(notificationId);
        if (!existing || existing.status === NotificationStatus.DISMISSED) {
          return {};
        }

        const updated = new Map(state.notifications);
        updated.set(notificationId, {
          ...existing,
          status: NotificationStatus.DISMISSED,
        });

        return {
          notifications: updated,
          unreadNotificationsCount: state.unreadNotificationsCount - 1,
        };
      }),
    notifications: new Map(),
    removeNotificationById: notificationId =>
      set(state => {
        const existing = state.notifications.get(notificationId);
        if (!existing) {
          return {};
        }

        const updated = new Map(state.notifications);
        updated.delete(notificationId);

        const unreadNotificationsCount =
          existing.status === NotificationStatus.SENT
            ? Math.max(0, state.unreadNotificationsCount - 1)
            : state.unreadNotificationsCount;

        return { notifications: updated, unreadNotificationsCount };
      }),
    setNotifications: (notifications, unreadCount) =>
      set(() => ({
        notifications: new Map(notifications.map(notification => [notification.id, notification])),
        unreadNotificationsCount: unreadCount,
      })),
    unreadNotificationsCount: 0,
  }),
  {
    name: 'notifications-store',
    storage: createSerializableStorage(storage),
  },
);

export const useUnreadNotificationsCount = (): number => useNotificationStore(state => state.unreadNotificationsCount);
